import { uniqBy } from 'lodash';

import { AfterDeath, EntityInEstateStatus, EntityKind } from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';
import { getNodes } from '@/utils/graphqlUtils';
import { getPulidKind, PulidKind } from '@/utils/pulid';

import {
  DiagramDispositionsFormRecipient,
  DiagramDispositionsFormState,
  NonRootRecipientOverride,
} from '../dispositiveProvisions/contexts/DispositiveDiagramSync.context';
import { RecipientKind } from '../dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsForm.types';
import { BdgMissingBeneficiaryTier } from './bdgActiveTier';
import {
  makeDiagramDispositionsFormRecipientFromEntity,
  makeDiagramDispositionsFormRecipientFromFragment,
  NormalizedDispositions,
  processEntityDispositions,
} from './getDispositiveProvisionsVizFromEntity/dispositions';
import { makeNodeFromRecipient } from './getDispositiveProvisionsVizFromEntity/nodes';
import {
  DispositionsDiagram_DispositionProvisionTerminalFragment,
  DispositionsDiagram_DispositionScenarioFirstDeathFragment,
} from './graphql/DispositionsDiagram.generated';
import {
  EntityDiagram_EdgeFragment,
  EntityDiagram_NodeFragment,
} from './types';

export type RecipientDispositions = Record<
  string,
  {
    firstDeath: DispositionsDiagram_DispositionProvisionTerminalFragment[];
    secondDeath: DispositionsDiagram_DispositionProvisionTerminalFragment[];
    missingBeneficiaryTier?: BdgMissingBeneficiaryTier;
    hasUnrenderedDispositions?: boolean;
  }
>;

export interface GetDispositionsVizFromEntityInput {
  // The root entity and its dispositions as they are returned from the server
  entity: {
    id: string;
    entityKind: EntityKind;
    extendedDisplayKind: string;
    subtype: {
      id: string;
      displayName: string;
      inEstateStatus?: EntityInEstateStatus | null;
      dispositionScenarios?:
        | DispositionsDiagram_DispositionScenarioFirstDeathFragment[]
        | null;
    };
  };
  // The id of the first grantor death which is used to find the relevant disposition scenario
  firstGrantorDeathId: string;
  // Whether the household has two clients
  isTwoClientHousehold: boolean;
  // When the recipient is the surviving spouse sentinel, we use this to create
  // their node in the diagram
  survivingSpouse?: {
    id: string;
    displayName: string;
  };
  // Used to control if we show warnings or not
  isOnDispositionsFormDiagram?: boolean;
  // External dispositions from the form that override the server dispositions
  externalDispositions?: DiagramDispositionsFormState;
  // Map of entity id to dispositions for that entity that correspond to the
  // various recipients in the external dispositions from the form
  recipientDispositions?: RecipientDispositions;
  // Live overlay for a non-root BDG entity being edited; overrides the
  // server-resolved dispositions for that entity id only.
  nonRootRecipientOverride?: NonRootRecipientOverride | null;
}

interface GetDispositionsVizFromEntityOutput {
  nodes: EntityDiagram_NodeFragment[];
  edges: EntityDiagram_EdgeFragment[];
}

interface NormalizeInputsInput {
  entity: GetDispositionsVizFromEntityInput['entity'];
  firstGrantorDeathId: string;
  externalDispositions?: DiagramDispositionsFormState;
  recipientDispositions?: RecipientDispositions;
  nonRootRecipientOverride?: NonRootRecipientOverride | null;
  survivingSpouse: { id: string; displayName: string };
}

// Use the external dispositions if they exist, otherwise use the server dispositions
function normalizeInputs({
  entity,
  firstGrantorDeathId,
  externalDispositions,
  recipientDispositions,
  nonRootRecipientOverride,
  survivingSpouse,
}: NormalizeInputsInput): {
  // The dispositions for the recipients server and form combined
  allDispositions: Record<string, NormalizedDispositions>;
} {
  // Convert all possible dispositions into a normalized shape for easy lookup later
  let allDispositions: Record<string, NormalizedDispositions> = {};

  const relevantScenario = entity.subtype.dispositionScenarios?.find(
    (scenario) => scenario.firstGrantorDeath?.id === firstGrantorDeathId
  );

  // Aggregate the dispositions for the entity from the server
  if (relevantScenario) {
    const firstDeathNodes = getNodes(
      relevantScenario.dispositiveProvisionsTemplate?.dispositiveProvisions ??
        relevantScenario.dispositiveProvisions
    );

    allDispositions = {
      ...allDispositions,
      [entity.id]: {
        firstDeath: firstDeathNodes.map((d) =>
          makeDiagramDispositionsFormRecipientFromFragment(
            d,
            survivingSpouse,
            firstGrantorDeathId
          )
        ),
        secondDeath: getNodes(
          relevantScenario.secondDeathDispositiveProvisionsTemplate
            ?.dispositiveProvisions ??
            relevantScenario.secondDeathDispositiveProvisions
        ).map((d) =>
          makeDiagramDispositionsFormRecipientFromFragment(
            d,
            survivingSpouse,
            firstGrantorDeathId
          )
        ),
      },
    };

    // For each node in the first death section, if it has dispositions from the server, add those to allDispositions
    firstDeathNodes.forEach((node) => {
      if (
        node.individual ||
        node.entity ||
        node.testamentaryEntity ||
        node.recipientKind === RecipientKind.SurvivingSpouse
      ) {
        const id =
          node.individual?.id ||
          node.entity?.id ||
          node.testamentaryEntity?.id ||
          (node.recipientKind === RecipientKind.SurvivingSpouse
            ? survivingSpouse?.id
            : null);

        if (!id) {
          return;
        }
        const dispositionScenarios =
          node.individual?.dispositionScenarios ||
          node.entity?.subtype.dispositionScenarios ||
          node.testamentaryEntity?.dispositionScenarios;

        const relevantScenario = dispositionScenarios?.find(
          (scenario) => scenario.firstGrantorDeath?.id === firstGrantorDeathId
        );

        if (relevantScenario) {
          allDispositions = {
            ...allDispositions,
            [id]: {
              firstDeath: getNodes(
                relevantScenario.dispositiveProvisionsTemplate
                  ?.dispositiveProvisions ??
                  relevantScenario.dispositiveProvisions
              ).map((d) =>
                makeDiagramDispositionsFormRecipientFromFragment(
                  d,
                  survivingSpouse,
                  firstGrantorDeathId
                )
              ),
              secondDeath: getNodes(
                relevantScenario.secondDeathDispositiveProvisionsTemplate
                  ?.dispositiveProvisions ??
                  relevantScenario.secondDeathDispositiveProvisions
              ).map((d) =>
                makeDiagramDispositionsFormRecipientFromFragment(
                  d,
                  survivingSpouse,
                  firstGrantorDeathId
                )
              ),
            },
          };
        }
      }
    });
  }

  const normalizedRecipientDispositions = Object.fromEntries(
    Object.entries(recipientDispositions || {}).map(([id, dispositions]) => {
      // A warning-only entry must not erase dispositions extracted from the
      // query above. Full query/form updates do not carry this metadata.
      if (
        dispositions.hasUnrenderedDispositions !== undefined &&
        dispositions.firstDeath.length === 0 &&
        dispositions.secondDeath.length === 0 &&
        allDispositions[id]
      ) {
        return [
          id,
          {
            ...allDispositions[id],
            hasUnrenderedDispositions: dispositions.hasUnrenderedDispositions,
          },
        ];
      }
      return [
        id,
        {
          firstDeath: dispositions.firstDeath.map((d) =>
            makeDiagramDispositionsFormRecipientFromFragment(
              d,
              survivingSpouse,
              firstGrantorDeathId
            )
          ),
          secondDeath: dispositions.secondDeath.map((d) =>
            makeDiagramDispositionsFormRecipientFromFragment(
              d,
              survivingSpouse,
              firstGrantorDeathId
            )
          ),
          missingBeneficiaryTier: dispositions.missingBeneficiaryTier,
          hasUnrenderedDispositions: dispositions.hasUnrenderedDispositions,
        },
      ];
    })
  );

  allDispositions = {
    ...allDispositions,
    ...normalizedRecipientDispositions,
  };

  // Live overlay for a non-root BDG entity being edited: override the
  // server-resolved dispositions for that entity id so its unsaved edits
  // cascade in the diagram. Its id differs from the root's `entity.id`, so
  // this never conflicts with the root `externalDispositions` write below.
  if (nonRootRecipientOverride) {
    allDispositions = {
      ...allDispositions,
      [nonRootRecipientOverride.entityId]: {
        firstDeath: nonRootRecipientOverride.firstDeath,
        secondDeath: nonRootRecipientOverride.secondDeath,
        missingBeneficiaryTier: nonRootRecipientOverride.missingBeneficiaryTier,
      },
    };
  }

  // Form overrides at the root take precedence over server-derived data.
  if (externalDispositions) {
    allDispositions = {
      ...allDispositions,
      [entity.id]: {
        firstDeath: externalDispositions.firstDeath || [],
        secondDeath: externalDispositions.secondDeath || [],
        missingBeneficiaryTier:
          externalDispositions.bdgRootMissingBeneficiaryTier,
      },
    };
  }

  return {
    allDispositions,
  };
}

// Determines where to plant the root of the entity diagram tree
function determineRootPlacement(
  entity: GetDispositionsVizFromEntityInput['entity']
): AfterDeath {
  const isTestamentaryEntity =
    getPulidKind(entity.id) === PulidKind.TestamentaryEntity;

  // Testamentary entities start in First section (don't exist before death)
  return isTestamentaryEntity ? AfterDeath.First : AfterDeath.None;
}

export function getDispositionsVizFromEntity({
  entity,
  firstGrantorDeathId,
  isTwoClientHousehold,
  survivingSpouse,
  isOnDispositionsFormDiagram,
  externalDispositions,
  recipientDispositions,
  nonRootRecipientOverride,
}: GetDispositionsVizFromEntityInput): GetDispositionsVizFromEntityOutput {
  // Normalize inputs into consistent shape to remove difference between dispositions from the form and dispositions from the server
  const { allDispositions } = normalizeInputs({
    entity,
    firstGrantorDeathId,
    externalDispositions,
    recipientDispositions,
    nonRootRecipientOverride,
    survivingSpouse: survivingSpouse ?? {
      id: '',
      displayName: '',
    },
  });

  // Determine root placement (i.e., testamentary entities start in First section)
  const rootPlacement = determineRootPlacement(entity);

  // Initialize output collections
  const nodes: EntityDiagram_NodeFragment[] = [];
  const edges: EntityDiagram_EdgeFragment[] = [];

  const rootDispositions = allDispositions[entity.id] ?? {
    firstDeath: [],
    secondDeath: [],
  };

  // The root recipient as a DiagramDispositionsFormRecipient so processEntityDispositions does not need to know about the server type
  const rootRecipient: DiagramDispositionsFormRecipient =
    makeDiagramDispositionsFormRecipientFromEntity(entity);

  const rootEntity: EntityDiagram_NodeFragment | null = makeNodeFromRecipient({
    recipient: rootRecipient,
    afterDeath: rootPlacement,
    survivingSpouse,
    firstGrantorDeathId,
    hasIncomingPouroverDisposition: false,
  });

  if (!rootEntity) {
    diagnostics.error('No root entity node found');
    return {
      nodes: [],
      edges: [],
    };
  }

  // Process entity dispositions recursively
  processEntityDispositions(
    {
      entity: { ...rootEntity },
      dispositions: rootDispositions ?? {
        firstDeath: [],
        secondDeath: [],
      },
      afterDeath: rootPlacement,
    },
    firstGrantorDeathId,
    isTwoClientHousehold,
    survivingSpouse,
    isOnDispositionsFormDiagram || false,
    nodes,
    edges,
    allDispositions,
    false
  );

  // Step 4: Add any missing section placeholders (only if needed for layout)
  const sectionsRepresented = new Set(nodes.map((n) => n.afterDeath));

  // Only add placeholders for sections that should exist but are missing a node
  if (!sectionsRepresented.has(AfterDeath.None)) {
    nodes.push({
      id: 'node-0',
      afterDeath: AfterDeath.None,
      node: null,
    });
  }

  if (!sectionsRepresented.has(AfterDeath.First)) {
    nodes.push({
      id: 'node-1',
      afterDeath: AfterDeath.First,
      node: null,
    });
  }

  if (!sectionsRepresented.has(AfterDeath.Second) && isTwoClientHousehold) {
    nodes.push({
      id: 'node-2',
      afterDeath: AfterDeath.Second,
      node: null,
    });
  }

  // Remove duplicates and sort for consistent output
  const uniqueNodes = uniqBy(nodes, (n) => `${n.id}-${n.afterDeath}`);
  const uniqueEdges = uniqBy(
    edges,
    (e) => `${e.from.id}-${e.from.afterDeath}-${e.to.id}-${e.to.afterDeath}`
  );

  return {
    nodes: uniqueNodes,
    edges: uniqueEdges,
  };
}
